// `useInboxJk` — D24 / US-248.
//
// Power-user Vim-style keyboard navigation for the inbox conversation list:
//
//   - `j` → select next conversation, scroll into view.
//   - `k` → select previous conversation, scroll into view.
//
// Mounted at the InboxPage level so the bindings fire whether focus is in the
// left-rail listbox OR inside the thread reader on the right. The DS-D
// `ConvList` already owns ArrowUp/ArrowDown/Home/End inside the listbox — j/k
// is a layered convenience on top, not a replacement.
//
// Guards (Vim convention):
//   - Skip when an input / textarea / contenteditable owns focus (so typing
//     "j" in the reply composer or the search bar doesn't move selection).
//   - Skip when modifier keys (Ctrl / Meta / Alt) are held — those belong to
//     OS / browser shortcuts.
//   - Skip when the active element is something we don't recognise but is
//     marked `contenteditable` (e.g. the DraftPanel editable surface).
//
// Edge cases (match the DS convention — see ConvList `moveFocus`):
//   - j at the last conv: no-op. We do NOT auto-fetch the next page; the
//     listbox-level ArrowDown clamps the same way, so j matches that.
//   - k at the first conv: no-op.
//   - Empty list / no selection: j seeds to the first item, k seeds to the
//     last. This mirrors how Gmail / Linear behave when you arrive on an
//     unselected list and tap j.

import { useEffect } from 'react';

import type { ConversationListItem } from '@barel/api-client/react';

export interface UseInboxJkOptions {
  /** Flattened list of conversations as rendered in the rail. */
  items: ConversationListItem[];
  /** Currently selected conversation id (null when nothing is selected). */
  selectedId: string | null;
  /** Setter — drives the URL search-param `selectedId`. */
  onSelect: (id: string) => void;
}

/**
 * Returns true when the event target is an editable surface where Vim-style
 * j/k must NOT hijack the key. We treat the following as editable:
 *
 *   - native `<input>` / `<textarea>` / `<select>`
 *   - any element with `contenteditable="true"` (or inherited)
 *   - the active element when it differs from `event.target` (covers shadow
 *     DOM / portal cases where the keydown bubbles to document)
 */
function isEditableTarget(event: KeyboardEvent): boolean {
  const target = event.target;
  const active = document.activeElement;

  const candidates: (EventTarget | null)[] = [target, active];
  for (const node of candidates) {
    if (!(node instanceof HTMLElement)) continue;
    if (
      node instanceof HTMLInputElement ||
      node instanceof HTMLTextAreaElement ||
      node instanceof HTMLSelectElement
    ) {
      return true;
    }
    if (node.isContentEditable) {
      return true;
    }
  }
  return false;
}

/**
 * Scroll the row for `id` into view (block: 'nearest' — minimal motion).
 * Looks up via the `data-conv-id` attribute the DS `ConvRow` already emits.
 * Defers to the next frame so the new selection's styling has rendered before
 * we scroll (avoids scrolling to an out-of-flow position mid-state-flush).
 */
function scrollRowIntoView(id: string): void {
  requestAnimationFrame(() => {
    const node = document.querySelector(`[data-conv-id="${CSS.escape(id)}"]`);
    if (node instanceof HTMLElement) {
      node.scrollIntoView({ block: 'nearest' });
    }
  });
}

export function useInboxJk({ items, selectedId, onSelect }: UseInboxJkOptions): void {
  useEffect(() => {
    if (items.length === 0) return;

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key !== 'j' && event.key !== 'k') return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isEditableTarget(event)) return;

      const currentIndex = selectedId ? items.findIndex((c) => c.id === selectedId) : -1;
      let nextIndex: number;

      if (event.key === 'j') {
        // Down. From "no selection" we seed to the first item.
        nextIndex = currentIndex < 0 ? 0 : currentIndex + 1;
        if (nextIndex >= items.length) return; // clamp at bottom — no-op
      } else {
        // 'k' — up. From "no selection" we seed to the last item.
        nextIndex = currentIndex < 0 ? items.length - 1 : currentIndex - 1;
        if (nextIndex < 0) return; // clamp at top — no-op
      }

      const next = items[nextIndex];
      if (!next) return;
      event.preventDefault();
      onSelect(next.id);
      scrollRowIntoView(next.id);
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [items, selectedId, onSelect]);
}
